import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getIn, useFormikContext } from 'formik';
import { Autocomplete, Box, IconButton, Typography } from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import { Text } from '@app/ui/forms';
import { Tooltip } from '@app/ui/tooltip';

import { BOOLEAN_VALUES } from '../constants';
import { ConditionFormValue, ConditionRowErrorTarget, RuleField, RuleOperator } from '../types';
import { conditionRowIssue, getConditionValueControl, legalOperatorsFor, operatorWireName } from '../utils';

export interface ConditionRowProps {
  readonly name: string;
  readonly index: number;
  readonly fields: RuleField[];
  readonly fieldsByPath: Map<string, RuleField>;
  readonly disabled: boolean;
  readonly canRemove: boolean;
  readonly canMoveUp: boolean;
  readonly canMoveDown: boolean;
  readonly onRemove: () => void;
  readonly onMove: (direction: -1 | 1) => void;
}

export const ConditionRow = ({
  name,
  index,
  fields,
  fieldsByPath,
  disabled,
  canRemove,
  canMoveUp,
  canMoveDown,
  onRemove,
  onMove,
}: ConditionRowProps) => {
  const { t } = useTranslation('common');
  const { values, setFieldValue, setFieldTouched, submitCount, touched } = useFormikContext<unknown>();

  const row = getIn(values, name) as ConditionFormValue | undefined;
  const field = row ? fieldsByPath.get(row.field) : undefined;
  // `legalOperatorsFor` builds a NEW array every render, so without this the `operatorOptions` memo
  // below could never hit.
  const operators = useMemo(() => legalOperatorsFor(field), [field?.path, field?.operators]);
  const control = getConditionValueControl(field?.type, row?.operator);
  // Shown once the row has been touched as well as after a submit attempt: autosave submits on a
  // 5s debounce, so waiting for `submitCount` left an incomplete row unmarked until then.
  const showError = submitCount > 0 || !!getIn(touched, name);
  const issue = showError && row ? conditionRowIssue(row, fieldsByPath, key => t(key) ?? key) : undefined;
  const errorFor = (target: ConditionRowErrorTarget) => (issue?.target === target ? issue.message : undefined);
  const fieldError = errorFor(ConditionRowErrorTarget.field);
  const operatorError = errorFor(ConditionRowErrorTarget.operator);
  const valueError = errorFor(ConditionRowErrorTarget.value);
  const markTouched = (suffix: string) => setFieldTouched(`${name}.${suffix}`, true);

  const operatorOptions = useMemo(
    () =>
      operators.map(operator => ({
        id: operator,
        name: t(`rules.operator.option.${operatorWireName(operator)}`) ?? operator,
      })),
    [operators, t],
  );

  const optionChoices = useMemo(
    () => (field?.options ?? []).map(option => ({ id: option.key, name: option.label })),
    [field],
  );

  if (!row) {
    return null;
  }

  // The field change is applied as ONE `setFieldValue` on the whole row, not as an effect chain: an
  // operator left over from the previous field is illegal for the new one and the server rejects
  // the whole rule (SPEC AC-14).
  const handleFieldChange = (next: RuleField | null) =>
    setFieldValue(name, { ...row, field: next?.path ?? '', operator: '', value: '', values: [] });

  const handleOperatorChange = (next: string) =>
    setFieldValue(name, { ...row, operator: next as RuleOperator, value: '', values: [] });

  return (
    <Box sx={{ border: '1px solid', borderColor: 'grey.300', borderRadius: '16px', p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="h4">{t('rules.editor.condition.heading', { number: index + 1 })}</Typography>

        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title={t('rules.editor.condition.moveUpButton') ?? undefined}>
            <span>
              <IconButton size="small" disabled={disabled || !canMoveUp} onClick={() => onMove(-1)}>
                <ArrowUpwardIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={t('rules.editor.condition.moveDownButton') ?? undefined}>
            <span>
              <IconButton size="small" disabled={disabled || !canMoveDown} onClick={() => onMove(1)}>
                <ArrowDownwardIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={t('rules.editor.condition.removeButton') ?? undefined}>
            <span>
              <IconButton size="small" disabled={disabled || !canRemove} onClick={onRemove}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      <Autocomplete
        disabled={disabled}
        options={fields}
        value={field ?? null}
        groupBy={option => t(`rules.editor.appliesTo.${option.appliesTo}`) ?? option.appliesTo}
        getOptionLabel={option => option.label}
        isOptionEqualToValue={(option, selected) => option.path === selected.path}
        onChange={(_event, next) => handleFieldChange(next)}
        onBlur={() => markTouched('field')}
        renderOption={(props, option) => (
          <li {...props} key={option.path}>
            <Box>
              <Typography variant="body1">{option.label}</Typography>
              <Typography variant="body2" color="text.secondary">
                {t('rules.editor.condition.fieldMeta', {
                  path: option.path,
                  type: t(`rules.fieldType.option.${option.type}`),
                })}
              </Typography>
            </Box>
          </li>
        )}
        renderInput={params => (
          <Text
            {...params}
            label={t('rules.editor.condition.fieldLabel')}
            error={!!fieldError}
            helperText={fieldError ?? t('rules.editor.condition.fieldHelperText')}
          />
        )}
      />

      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 2, mt: 2 }}>
        {/* Same one-line helper-text sizing as the Trigger logic select; this one measures 267px. */}
        <Box sx={theme => ({ width: { xs: '100%', sm: theme.spacing(36) } })}>
          <Text
            select
            disabled={disabled || !field}
            label={t('rules.editor.condition.operatorLabel')}
            error={!!operatorError}
            helperText={operatorError ?? t('rules.editor.condition.operatorHelperText')}
            value={row.operator}
            onChange={event => handleOperatorChange(event.target.value)}
            onBlur={() => markTouched('operator')}
            options={operatorOptions}
          />
        </Box>

        {/* One `onBlur` on the wrapper rather than on each of the five variants — React's blur
        bubbles, and the value control is one control whichever variant is rendered. */}
        <Box sx={{ flexGrow: 1, minWidth: 240 }} onBlur={() => markTouched('value')}>
          {control === 'none' && !!row.operator && (
            <Typography variant="body2" color="text.secondary">
              {t('rules.editor.condition.noValueNeeded')}
            </Typography>
          )}

          {control === 'boolean' && (
            <Text
              select
              disabled={disabled}
              label={t('rules.editor.condition.valueLabel')}
              error={!!valueError}
              helperText={valueError}
              value={row.value}
              onChange={event => setFieldValue(`${name}.value`, event.target.value)}
              options={BOOLEAN_VALUES.map(value => ({
                id: value,
                name: t(`general.option.${value === 'true' ? 'yes' : 'no'}`) ?? value,
              }))}
            />
          )}

          {control === 'optionSelect' && (
            <Text
              select
              disabled={disabled}
              label={t('rules.editor.condition.valueLabel')}
              error={!!valueError}
              helperText={valueError ?? t('rules.editor.condition.optionValueHelperText')}
              value={row.value}
              onChange={event => setFieldValue(`${name}.value`, event.target.value)}
              options={optionChoices}
            />
          )}

          {control === 'optionMultiSelect' && (
            <Text
              select
              disabled={disabled}
              SelectProps={{ multiple: true }}
              label={t('rules.editor.condition.valuesLabel')}
              error={!!valueError}
              helperText={valueError ?? t('rules.editor.condition.optionValueHelperText')}
              value={row.values}
              onChange={event => setFieldValue(`${name}.values`, event.target.value)}
              options={optionChoices}
            />
          )}

          {control === 'number' && (
            <Text
              disabled={disabled}
              label={t('rules.editor.condition.valueLabel')}
              error={!!valueError}
              helperText={valueError ?? t('rules.editor.condition.numberValueHelperText')}
              value={row.value}
              onChange={event => setFieldValue(`${name}.value`, event.target.value)}
            />
          )}

          {control === 'text' && (
            <Text
              disabled={disabled}
              label={t('rules.editor.condition.valueLabel')}
              error={!!valueError}
              helperText={valueError ?? t('rules.editor.condition.textValueHelperText')}
              value={row.value}
              onChange={event => setFieldValue(`${name}.value`, event.target.value)}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};
